import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ConfirmationModal } from "@/components/ui/confirmation-modal";
import { DetailHeader } from "@/components/layout/detail-header";
import { api } from "@/lib/api";
import { useToast } from "@/contexts/toast-context";
import { GripVertical, Trash2 } from "lucide-react";

interface Test {
  id: string;
  name: string;
  description?: string;
  group?: string;
  isAutomatable: boolean;
  createdAt: string;
  credentials?: {
    apiKey?: string;
  };
}

interface Step {
  id: string;
  number: number;
  name: string;
  type: string;
  data: Record<string, any>;
  expanded?: boolean;
  isEdited?: boolean;
  isEditing?: boolean;
}

interface StepType {
  type: string;
  label: string;
  description: string;
  startStep: boolean;
}

interface FieldConfig {
  key: string;
  type: string;
  label: string;
  placeholder?: string;
  displayValues?: Array<{ key: string; value: string }>;
}

export function TestFlowPage() {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  
  const [test, setTest] = useState<Test | null>(null);
  const [steps, setSteps] = useState<Step[]>([]);
  const [stepTypes, setStepTypes] = useState<StepType[]>([]);
  const [fieldConfigs, setFieldConfigs] = useState<FieldConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingSteps, setIsLoadingSteps] = useState(false);
  const [draggedStep, setDraggedStep] = useState<Step | null>(null);
  const [dragOverStep, setDragOverStep] = useState<Step | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [stepToDelete, setStepToDelete] = useState<Step | null>(null);
  const [isDeletingStep, setIsDeletingStep] = useState(false);

  // Load test data and initial setup
  useEffect(() => {
    if (testId) {
      loadInitialData();
    }
  }, [testId]);

  const loadInitialData = async () => {
    try {
      setIsLoading(true);
      const [testData, stepsData, stepTypesData, fieldConfigsData] = await Promise.all([
        api.get<Test>(`/test/${testId}`),
        api.get<Step[]>(`/test/${testId}/steps`),
        api.get<StepType[]>(`/test/${testId}/steps/types`),
        api.get<FieldConfig[]>(`/test/${testId}/steps/types/components`)
      ]);

      setTest(testData);
      setSteps(stepsData || []);
      setStepTypes(stepTypesData || []);
      setFieldConfigs(fieldConfigsData || []);
    } catch (error) {
      console.error("Failed to load initial data:", error);
      showError("Failed to load test details", "Could not load test information. Please try again.");
      navigate("/app/tests");
    } finally {
      setIsLoading(false);
    }
  };

  const loadSteps = async () => {
    if (!testId) return;
    
    try {
      setIsLoadingSteps(true);
      const stepsData = await api.get<Step[]>(`/test/${testId}/steps`);
      setSteps(stepsData || []);
    } catch (error) {
      console.error('Error loading steps:', error);
      showError("Error loading steps", "Could not load steps. Please try again.");
      setSteps([]);
    } finally {
      setIsLoadingSteps(false);
    }
  };

  const handleCreateStep = async () => {
    if (!testId) return;
    
    try {
      // Calculate the next step number
      const nextStepNumber = steps.length > 0 ? Math.max(...steps.map(s => s.number)) + 1 : 1;
      
      const newStep = await api.post<Step>(`/test/${testId}/steps`, {
        step: {
          number: nextStepNumber,
          name: 'New Step'
        }
      });
      
      setSteps(prev => [...prev, newStep]);
      showSuccess("Step created", "New step created successfully");
    } catch (error) {
      console.error('Error creating step:', error);
      showError("Failed to create step", "Could not create new step. Please try again.");
    }
  };

  const moveStep = async (draggedStep: Step, newNumber: number) => {
    try {
      await api.post(`/test/${testId}/steps/move`, {
        stepNumber: draggedStep.number,
        newStepNumber: newNumber
      });
      
      showSuccess("Step moved", "Step moved successfully");
      loadSteps();
    } catch (error) {
      console.error("Failed to move step:", error);
      showError("Failed to move step", "Could not move step. Please try again.");
    }
  };

  const handleDragStart = (step: Step) => {
    setDraggedStep(step);
  };

  const handleDragOver = (step: Step, e: React.DragEvent) => {
    e.preventDefault();
    setDragOverStep(step);
  };

  const handleDragEnd = () => {
    setDraggedStep(null);
    setDragOverStep(null);
  };

  const confirmDelete = (step: Step) => {
    setStepToDelete(step);
    setShowDeleteModal(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setStepToDelete(null);
  };

  const handleDeleteStep = async () => {
    if (!stepToDelete) return;
    
    try {
      setIsDeletingStep(true);
      await api.delete(`/test/${testId}/steps/${stepToDelete.number}`);
      showSuccess("Step deleted", "Step deleted successfully");
      loadSteps();
      setShowDeleteModal(false);
      setStepToDelete(null);
    } catch (error) {
      console.error("Failed to delete step:", error);
      showError("Failed to delete step", "Could not delete step. Please try again.");
    } finally {
      setIsDeletingStep(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    
    if (!draggedStep || !dragOverStep || !testId) return;
    
    if (draggedStep.id !== dragOverStep.id) {
      await moveStep(draggedStep, dragOverStep.number);
    }
    
    setDraggedStep(null);
    setDragOverStep(null);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-center flex-1">
          <div className="text-sm text-muted-foreground">Loading test details...</div>
        </div>
      </div>
    );
  }

  if (!test) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-center flex-1">
          <div className="text-sm text-muted-foreground">Test not found</div>
        </div>
      </div>
    );
  }

  const breadcrumb = [
    { label: "All Tests", href: "/app/tests" },
    { label: test.name }
  ];

  const toggleOptions = [
    { id: "details", label: "Details" },
    { id: "explorer", label: "Explorer" },
    { id: "files", label: "Files" },
    { id: "flow", label: "Flow" },
    { id: "expectations", label: "Expectations" },
    { id: "runs", label: "Runs" },
  ];

  const headerActions = (
    <Button
      onClick={handleCreateStep}
      size="sm"
      className="text-xs"
    >
      New Step
    </Button>
  );

  const handleToggleChange = (tabId: string) => {
    if (tabId === "details") {
      navigate(`/app/tests/${testId}`);
    } else {
      navigate(`/app/tests/${testId}/${tabId}`);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <DetailHeader
        breadcrumb={breadcrumb}
        toggleOptions={toggleOptions}
        activeToggle="flow"
        onToggleChange={handleToggleChange}
        actions={headerActions}
      />
      <div className="flex-1 p-6" style={{ overflow: 'visible auto' }}>
        {isLoadingSteps ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-sm text-muted-foreground">Loading steps...</div>
          </div>
        ) : steps.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <div className="text-sm">No steps defined.</div>
            <div className="text-xs mt-1">Click "New Step" to create one.</div>
          </div>
        ) : (
          <div className="space-y-4">
            {steps
              .sort((a, b) => a.number - b.number)
              .map((step) => (
                <StepItem
                  key={step.id}
                  step={step}
                  stepTypes={stepTypes}
                  fieldConfigs={fieldConfigs}
                  testId={testId || ""}
                  onStepUpdate={loadSteps}
                  onError={showError}
                  onSuccess={showSuccess}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                        onDragEnd={handleDragEnd}
                        onDrop={handleDrop}
                        isDraggedOver={dragOverStep?.id === step.id}
                        onConfirmDelete={confirmDelete}
                      />
              ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteStep}
        title="Delete Step"
        message={`Are you sure you want to delete step ${stepToDelete?.number}: "${stepToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeletingStep}
      />
    </div>
  );
}

interface StepItemProps {
  step: Step;
  stepTypes: StepType[];
  fieldConfigs: FieldConfig[];
  testId: string;
  onStepUpdate: () => Promise<void>;
  onError: (title: string, message: string) => void;
  onSuccess: (title: string, message: string) => void;
  onDragStart: (step: Step) => void;
  onDragOver: (step: Step, e: React.DragEvent) => void;
  onDragEnd: () => void;
  onDrop: (e: React.DragEvent) => void;
  isDraggedOver: boolean;
  onConfirmDelete: (step: Step) => void;
}

function StepItem({ 
  step: initialStep, 
  stepTypes, 
  fieldConfigs, 
  testId, 
  onStepUpdate, 
  onError, 
  onSuccess, 
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop,
  isDraggedOver,
  onConfirmDelete
}: StepItemProps) {
  const [step, setStep] = useState(initialStep);
  const [showStepTypeMenu, setShowStepTypeMenu] = useState(false);
  const [stepTypeSearch, setStepTypeSearch] = useState("");

  // Update local state when prop changes
  useEffect(() => {
    setStep(initialStep);
  }, [initialStep]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.step-type-dropdown')) {
        setShowStepTypeMenu(false);
      }
    };

    if (showStepTypeMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showStepTypeMenu]);

  const handleUpdateStep = async () => {
    try {
      await api.patch(`/test/${testId}/steps/${step.number}`, {
        step: {
          name: step.name,
          data: step.data
        }
      });
      
      setStep(prev => ({ ...prev, isEdited: false }));
      onSuccess("Step updated", "Step updated successfully");
      onStepUpdate();
    } catch (error) {
      console.error("Failed to update step:", error);
      onError("Failed to update step", "Could not update step. Please try again.");
    }
  };


  const handleToggleExpansion = () => {
    setStep(prev => ({ ...prev, expanded: !prev.expanded }));
  };

  const currentStepType = stepTypes.find(type => type.type === step.type);

  return (
    <div className={`border rounded-lg transition-all ${
      isDraggedOver ? 'border-blue-500 border-dashed' : 'border-border'
    }`} style={{ overflow: 'visible' }}>
      <div 
        className="flex items-center gap-4 p-4 cursor-pointer hover:bg-muted" 
        onClick={handleToggleExpansion}
        draggable={!step.isEditing}
        onDragStart={(e) => {
          e.stopPropagation();
          onDragStart(step);
        }}
        onDragOver={(e) => {
          e.stopPropagation();
          onDragOver(step, e);
        }}
        onDrop={(e) => {
          e.stopPropagation();
          onDrop(e);
        }}
        onDragEnd={onDragEnd}
      >
        {/* Drag handle */}
        <div
          className="text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing p-1"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="w-4 h-4" />
        </div>
        <div className="text-sm font-medium text-muted-foreground w-8">
          {step.number}
        </div>
        
        <input
          type="text"
          value={step.name}
          onChange={(e) => {
            setStep(prev => ({ ...prev, name: e.target.value, isEdited: true }));
          }}
          onClick={(e) => e.stopPropagation()}
          className="flex-1 px-3 py-2 text-sm border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
          placeholder="Step name"
        />
        
        <div className="relative step-type-dropdown">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowStepTypeMenu(!showStepTypeMenu);
            }}
            className="w-[28rem] px-3 py-2 text-xs border border-input bg-background rounded-md hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring text-left flex items-center justify-between cursor-pointer"
          >
            <span className="truncate">
              {step.type === 'EMPTY' ? 'Select Step Type' : (currentStepType?.label || step.type.replace(/_/g, ' '))}
            </span>
            <span className="ml-2 flex-shrink-0">▼</span>
          </button>
          
          {showStepTypeMenu && (
            <div className="absolute right-0 top-full mt-1 w-[28rem] bg-background border border-border rounded-md shadow-xl z-[9999]" style={{ zIndex: 9999 }}>
              <div className="p-2 border-b border-border">
                <input
                  type="text"
                  value={stepTypeSearch}
                  onChange={(e) => setStepTypeSearch(e.target.value)}
                  placeholder="Search step types..."
                  className="w-full px-3 py-2 text-xs border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div className="max-h-64 overflow-y-auto">
                {stepTypes
                  .filter(type =>
                    type.label.toLowerCase().includes(stepTypeSearch.toLowerCase()) ||
                    type.description.toLowerCase().includes(stepTypeSearch.toLowerCase())
                  )
                  .map((type) => (
                    <button
                      key={type.type}
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const requestBody = { typeName: type.type };
                          
                          await api.patch(`/test/${testId}/steps/${step.number}/type`, requestBody);
                          
                          setShowStepTypeMenu(false);
                          setStepTypeSearch("");
                          onSuccess("Step type updated", "Step type updated successfully");
                          onStepUpdate();
                        } catch (error) {
                          console.error("Failed to update step type:", error);
                          const errorMessage = error instanceof Error ? error.message : String(error);
                          console.error("Error details:", errorMessage);
                          onError("Failed to update step type", `Could not update step type: ${errorMessage}`);
                        }
                      }}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-muted border-b border-border last:border-b-0 cursor-pointer"
                    >
                      <div className="font-medium">{type.label}</div>
                      <div className="text-xs text-muted-foreground mt-1">{type.description}</div>
                    </button>
                  ))}
              </div>
            </div>
          )}
        </div>
        
        <Button
          onClick={(e) => {
            e.stopPropagation();
            handleUpdateStep();
          }}
          disabled={!step.isEdited}
          size="sm"
          variant="secondary"
          className="text-xs h-7"
        >
          Save
        </Button>
        
        <button
          onClick={(e) => {
            e.stopPropagation();
            onConfirmDelete(step);
          }}
          className="px-2 py-1 text-xs text-destructive hover:bg-muted rounded-md cursor-pointer"
          title="Delete step"
        >
          <Trash2 className="w-4 h-4" />
        </button>
        
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleToggleExpansion();
          }}
          className="px-2 py-1 text-xs text-muted-foreground hover:bg-muted rounded-md cursor-pointer"
        >
          {step.expanded ? '▼' : '▶'}
        </button>
      </div>
      
      {step.expanded && (
        <div className="border-t border-border p-4">
          {step.type === 'EMPTY' ? (
            <div className="text-center py-8 text-muted-foreground">
              <div className="text-sm">Select a step type to configure this step.</div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                {currentStepType?.description || 'No description available'}
              </div>

              <DynamicStepForm
                step={step}
                fieldConfigs={fieldConfigs}
                onChange={(updatedData) => {
                  setStep(prev => ({
                    ...prev,
                    data: updatedData,
                    isEdited: true
                  }));
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface DynamicStepFormProps {
  step: Step;
  fieldConfigs: FieldConfig[];
  onChange: (updatedData: Record<string, any>) => void;
}

function DynamicStepForm({ step, fieldConfigs, onChange }: DynamicStepFormProps) {
  if (!step.data || Object.keys(step.data).length === 0) {
    return (
      <div className="text-center py-4 text-muted-foreground">
        <div className="text-sm">No configuration fields available for this step type.</div>
      </div>
    );
  }

  const getFieldConfig = (key: string) => {
    return fieldConfigs.find(config => config.key === key);
  };

  const handleFieldChange = (key: string, value: any) => {
    const updatedData = {
      ...step.data,
      [key]: value
    };
    onChange(updatedData);
  };

  return (
    <div className="space-y-4">
      {Object.keys(step.data).map((key) => {
        const fieldConfig = getFieldConfig(key);
        const currentValue = step.data[key];
        
        if (!fieldConfig) {
          // Fallback for fields without configuration - treat as text input
          return (
            <div key={key} className="space-y-3">
              <label className="text-sm font-medium text-foreground capitalize">
                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
              </label>
              <input
                type="text"
                value={currentValue || ''}
                onChange={(e) => handleFieldChange(key, e.target.value)}
                className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder={`Enter ${key}`}
              />
            </div>
          );
        }

        switch (fieldConfig.type) {
          case 'array':
            return (
              <div key={key} className="space-y-3">
                <label className="text-sm font-medium text-foreground">
                  {fieldConfig.label}
                </label>
                <textarea
                  value={Array.isArray(currentValue) ? currentValue.join(', ') : currentValue || ''}
                  onChange={(e) => {
                    const arrayValue = e.target.value.split(',').map(item => item.trim()).filter(item => item);
                    handleFieldChange(key, arrayValue);
                  }}
                  className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder={fieldConfig.placeholder || 'Enter comma-separated values'}
                  rows={3}
                />
                <div className="text-xs text-muted-foreground">Enter comma-separated values</div>
              </div>
            );

          case 'multiselect': {
            const multiselectOptions = Array.isArray(fieldConfig.displayValues) ? fieldConfig.displayValues : [];
            return (
              <div key={key} className="space-y-3">
                <div className="max-h-48 overflow-y-auto border border-input rounded-md">
                  <div className="p-3 border-b border-border">
                    <label className="flex items-center text-sm">
                      <input
                        type="checkbox"
                        checked={multiselectOptions.length > 0 && currentValue && currentValue.length === multiselectOptions.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            handleFieldChange(key, multiselectOptions.map((v: any) => v.key));
                          } else {
                            handleFieldChange(key, []);
                          }
                        }}
                        className="mr-2"
                      />
                      <span className="font-medium ">{fieldConfig.label}</span>
                    </label>
                  </div>
                  <div className="p-2">
                    {multiselectOptions.length > 0 ? multiselectOptions.map((option: any) => (
                      <label key={option.key} className="flex items-center py-2 px-1 text-sm">
                        <input
                          type="checkbox"
                          checked={Array.isArray(currentValue) && currentValue.includes(option.key)}
                          onChange={(e) => {
                            const currentArray = Array.isArray(currentValue) ? currentValue : [];
                            if (e.target.checked) {
                              handleFieldChange(key, [...currentArray, option.key]);
                            } else {
                              handleFieldChange(key, currentArray.filter(id => id !== option.key));
                            }
                          }}
                          className="mr-2"
                        />
                        <span>{option.value}</span>
                      </label>
                    )) : (
                      <div className="text-sm text-muted-foreground p-2">No options available</div>
                    )}
                  </div>
                </div>
              </div>
            );
          }

          case 'select': {
            const selectOptions = Array.isArray(fieldConfig.displayValues) ? fieldConfig.displayValues : [];
            return (
              <div key={key} className="space-y-3">
                <div className="max-h-48 overflow-y-auto border border-input rounded-md">
                  <div className="p-3 border-b border-border">
                    <span className="text-sm font-medium ">{fieldConfig.label}</span>
                  </div>
                  <div className="p-2">
                    {selectOptions.length > 0 ? selectOptions.map((option: any) => (
                      <label key={option.key} className="flex items-center py-2 px-1 text-sm">
                        <input
                          type="radio"
                          name={key}
                          value={option.key}
                          checked={currentValue === option.key}
                          onChange={() => handleFieldChange(key, option.key)}
                          className="mr-2"
                        />
                        <span>{option.value}</span>
                      </label>
                    )) : (
                      <div className="text-sm text-muted-foreground p-2">No options available</div>
                    )}
                  </div>
                </div>
              </div>
            );
          }

          case 'textarea':
            return (
              <div key={key} className="space-y-3">
                <label className="text-sm font-medium text-foreground">
                  {fieldConfig.label}
                </label>
                <textarea
                  value={currentValue || ''}
                  onChange={(e) => handleFieldChange(key, e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder={fieldConfig.placeholder}
                  rows={4}
                />
              </div>
            );

          case 'dictionary':
            return (
              <DictionaryEditor
                key={key}
                fieldKey={key}
                label={fieldConfig.label}
                value={currentValue}
                onChange={handleFieldChange}
              />
            );

          default:
            // Default text input
            return (
              <div key={key} className="space-y-3">
                <label className="text-sm font-medium text-foreground">
                  {fieldConfig.label}
                </label>
                <input
                  type="text"
                  value={currentValue || ''}
                  onChange={(e) => handleFieldChange(key, e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder={fieldConfig.placeholder}
                />
              </div>
            );
        }
      })}
    </div>
  );
}

interface DictionaryEditorProps {
  fieldKey: string;
  label: string;
  value: any;
  onChange: (key: string, value: any) => void;
}

function DictionaryEditor({ fieldKey, label, value, onChange }: DictionaryEditorProps) {
  const dictValue = (typeof value === 'object' && value !== null) ? value : {};
  const [tempKeys, setTempKeys] = useState<Record<string, string>>({});

  return (
    <div className="space-y-3">
      <div className="border border-input rounded-md">
        <div className="flex justify-between items-center p-3 border-b border-border bg-muted/50">
          <span className="text-sm font-medium">{label}</span>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              const timestamp = Date.now().toString();
              const tempKey = `temp_${timestamp}`;
              const newDict = { ...dictValue, [tempKey]: '' };
              setTempKeys(prev => ({ ...prev, [tempKey]: '' }));
              onChange(fieldKey, newDict);
            }}
            className="text-xs"
          >
            Add Entry
          </Button>
        </div>
        <div className="p-2">
          {Object.keys(dictValue).length === 0 ? (
            <div className="text-sm text-muted-foreground p-4 text-center">
              No entries. Click "Add Entry" to create key-value pairs.
            </div>
          ) : (
            Object.entries(dictValue).map(([dictKey, dictVal]) => (
              <div key={`${fieldKey}-${dictKey}`} className="flex items-center gap-2 mb-2 p-2 border border-border rounded-md">
                <input
                  type="text"
                  value={tempKeys[dictKey] !== undefined ? tempKeys[dictKey] : dictKey}
                  onChange={(e) => {
                    setTempKeys(prev => ({ ...prev, [dictKey]: e.target.value }));
                  }}
                  onBlur={(e) => {
                    const newKey = e.target.value;
                    if (newKey !== dictKey && newKey.trim() !== '') {
                      const newDict = { ...dictValue };
                      delete newDict[dictKey];
                      newDict[newKey] = dictVal;
                      onChange(fieldKey, newDict);
                      setTempKeys(prev => {
                        const { [dictKey]: removed, ...rest } = prev;
                        return rest;
                      });
                    } else if (newKey.trim() === '') {
                      // Reset to original if empty
                      setTempKeys(prev => {
                        const { [dictKey]: removed, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                  className="flex-1 px-2 py-1 text-sm border border-input bg-background rounded focus:outline-none focus:ring-1 focus:ring-ring"
                  placeholder="Key"
                />
                <input
                  type="text"
                  value={dictVal as string}
                  onChange={(e) => {
                    const newDict = { ...dictValue };
                    newDict[dictKey] = e.target.value;
                    onChange(fieldKey, newDict);
                  }}
                  className="flex-1 px-2 py-1 text-sm border border-input bg-background rounded focus:outline-none focus:ring-1 focus:ring-ring"
                  placeholder="Value"
                />
                <button
                  onClick={() => {
                    const newDict = { ...dictValue };
                    delete newDict[dictKey];
                    onChange(fieldKey, newDict);
                    setTempKeys(prev => {
                      const { [dictKey]: removed, ...rest } = prev;
                      return rest;
                    });
                  }}
                  className="px-2 py-1 text-destructive hover:bg-muted rounded text-sm"
                >
                  ×
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}